//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { Box, Typography } from '@mui/material';
import StatusDropdown from './StatusDropdown';
import AssigneeSelect from '../_common/AssigneeSelect/AssigneeSelect';
import Notes from '../_common/Notes/Notes';
import ClickCopy from '../_common/ClickCopy/ClickCopy';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import { insertWordBreaks } from '../../_utils/utils';
import PendingAssetRowExpand from './PendingAssetRowExpand';
import History from '../_common/History/History';
import LinkAsset from './LinkAsset/LinkAsset';
import { Order, OrderJobAsset, OrderJobTask, UserProfile, TitleEntity } from '@/redux/slices/slice_types';
import { capitalize } from 'lodash';

export default {
  expandable: {
    label: '',
    isExpandable: true,
    render: (props: any) => <PendingAssetRowExpand {...props} />
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobAsset['hid']) => <strong>{cell}</strong>
  },
  jobs__due_date: {
    label: 'Due Date',
    width: 100,
    render: (_cell: any, row: OrderJobAsset) => <DateTimeDisplay>{row.jobs__due_date}</DateTimeDisplay>
  },
  jobs: {
    label: 'Title Name',
    sortField: 'jobs__title__meta__series_title',
    width: 200,
    render: (cell: OrderJobAsset['jobs']) => {
      if (!cell) return '-';
      const title = cell?.[0]?.title;
      if (!title) return '-';
      const { entity_type = 'feature', name = '', sequence_order, lineage_meta = {} } = title;
      return (
        <div style={{ lineHeight: 1.5 }}>
          <div>
            <strong>{insertWordBreaks(lineage_meta.series_name || name)}</strong>
          </div>
          <Typography variant="subtitle2" sx={{ fontSize: '.95rem', lineHeight: 1.4, mt: 0.5, opacity: 0.5, fontWeight: 500 }}>
            {entity_type === 'episode' && (
              <>
                S{lineage_meta.season_sequence_order} &bull; E{sequence_order} &bull;
              </>
            )}
            {lineage_meta.series_name && <>&quot;{name}&quot;</>}
          </Typography>
        </div>
      );
    }
  },
  filename: {
    label: 'Asset Name',
    width: 280,
    render: (cell: string, row: Order) => (
      <>
        <ClickCopy copyText={cell}>
          <strong>{insertWordBreaks(cell)}</strong>
        </ClickCopy>

        <Typography variant="subtitle2" sx={{ fontSize: '.95rem', lineHeight: 1.4, opacity: 0.5, fontWeight: 500 }}>
          {row.base_path || ''}
        </Typography>
      </>
    )
  },
  jobs__version__name: {
    label: 'Version',
    render: (cell: TitleEntity['name']) => insertWordBreaks(cell)
  },
  sub_type: {
    label: 'Sub Type',
    width: 100,
    disableSort: true,
    render: (cell: OrderJobAsset['sub_type']) => (cell?.toLowerCase() === 'scc' ? 'Caption' : capitalize(cell))
  },
  assigned_user: {
    label: 'Assignee',
    render: (cell: UserProfile, row: Order) => <AssigneeSelect value={cell} id={`${row.id}`} assigns="user" isAsset />,
    width: 200,
    disableSort: true
  },
  status: {
    label: 'Status',
    render: (cell: undefined, row: OrderJobAsset) => (
      <StatusDropdown id={row.id} status={row.status} statusReason={row.status_reason} />
    )
  },
  history: {
    label: 'History',
    disableSort: true,
    render: (cell: undefined, row: OrderJobTask) => (
      <Box sx={{ ml: 1 }}>
        <History data={row} iconOnly />
      </Box>
    )
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobAsset) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="pendingAssets" />
      </div>
    ),
    disableSort: true
  },
  link_action: {
    label: 'Link',
    disableSort: true,
    width: 150,
    render: (cell: undefined, row: Order) => <LinkAsset disabled={row.status !== 'awaiting_ingest'} data={row as any} />
  }
};
